import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useCallback, useState} from 'react';
import toast from '~/utils/toast';
import {
  ICreateInvoiceVariables,
  IGenerateInvoiceVariables,
  generateReceiptVariables,
  ISaveInvoiceVariables,
  ISaveInvoiceResponseData,
  postCreateInvoice,
  postGenerateInvoice,
  postGenerateReceipt,
  postUpdateInvoice,
  IUpdateInvoiceVariables,
  IInvoiceDetails,
  patchSaveInvoice,
  getSendReceipt,
} from '~/api/finance/invoice';
import {ToastType} from '~/components/Toast/constant';
import {handleErrorResponse} from '~/utils/axios';
import {UseMutationOptions} from '@tanstack/react-query';

export interface ICreateInvoiceHookParams {
  showNotification?: boolean;
  onCreateInvoiceSuccess?: () => void;
}

export const useCreateInvoice = (params?: ICreateInvoiceHookParams) => {
  const {showNotification = true, onCreateInvoiceSuccess} = params || {};
  const [isDisabled, setIsDisabled] = useState(false);
  const queryClient = useQueryClient();

  const {isPending, data, mutate} = useMutation({
    mutationKey: ['create-invoice'],
    mutationFn: postCreateInvoice,
    onSuccess: data => {
      if (showNotification) {
        toast(ToastType.Success, 'Invoice created', data.message);
      }
      if (onCreateInvoiceSuccess) onCreateInvoiceSuccess();
      queryClient.setQueryData(['generate-invoice'], data);
    },
    onError: error => handleErrorResponse(error, 'Create Invoice'),
    onSettled: () => setIsDisabled(false),
  });

  const sentCreateInvoiceRequest = useCallback(
    (variables: ICreateInvoiceVariables) => {
      setIsDisabled(true);
      mutate(variables);
    },
    [mutate]
  );

  return {
    isPending,
    invoiceData: data?.data?.invoice,
    isDisabled,
    createInvoice: sentCreateInvoiceRequest,
  };
};

export const useGenerateInvoice = () => {
  const {isPending, data, mutate} = useMutation({
    mutationKey: ['generate-invoice-pdf'],
    mutationFn: postGenerateInvoice,
    onError: error => handleErrorResponse(error, 'Generate Invoice'),
  });

  const generateInvoice = useCallback(
    (variables: IGenerateInvoiceVariables) => {
      mutate(variables);
    },
    [mutate]
  );

  return {
    isInvoiceGenerating: isPending,
    pdfData: data,
    generateInvoice,
  };
};

export const useGenerateReciept = () => {
  const {isPending, data, mutateAsync, isError} = useMutation({
    mutationKey: ['generate-receipt-pdf'],
    mutationFn: postGenerateReceipt,
    onError: error => handleErrorResponse(error, 'Generate Receipt'),
  });

  const generateReceipt = useCallback(
    async (variables: generateReceiptVariables) => {
      return await mutateAsync(variables);
    },
    [mutateAsync]
  );

  return {
    isError,
    isReceiptGenerating: isPending,
    receiptData: data,
    generateReceipt,
  };
};

interface IUpdateInvoiceResponse {
  data: {
    invoice: IInvoiceDetails;
  };
  message?: string;
}

type UpdateInvoiceOptions = UseMutationOptions<
  IUpdateInvoiceResponse,
  Error,
  IUpdateInvoiceVariables
>;

export const useUpdateInvoice = () => {
  const queryClient = useQueryClient();

  const {isPending, mutate} = useMutation<
    IUpdateInvoiceResponse,
    Error,
    IUpdateInvoiceVariables
  >({
    mutationKey: ['update-invoice'],
    mutationFn: postUpdateInvoice,
    onError: error => handleErrorResponse(error, 'Update Invoice'),
    onSuccess: data => {
      // Update the query cache with the new data
      queryClient.setQueryData(['generate-invoice'], data);

      const currentInvoiceData = queryClient.getQueryData<{
        data: {invoice: IInvoiceDetails};
      }>(['create-invoice']);
      if (currentInvoiceData && data?.data?.invoice) {
        queryClient.setQueryData(['create-invoice'], {
          ...currentInvoiceData,
          data: {
            ...currentInvoiceData.data,
            invoice: data.data.invoice,
          },
        });
      }

      queryClient.invalidateQueries({queryKey: ['generate-invoice-pdf']});
    },
  });

  const updateInvoice = useCallback(
    (variables: IUpdateInvoiceVariables, options?: UpdateInvoiceOptions) => {
      mutate(variables, options);
    },
    [mutate]
  );

  return {
    isPending,
    updateInvoice,
  };
};

export interface ISaveInvoiceHookParams {
  onSuccess?: (response: ISaveInvoiceResponseData) => void;
}

export const useSaveInvoice = (params?: ISaveInvoiceHookParams) => {
  const {onSuccess} = params || {};
  const queryClient = useQueryClient();

  const {isPending, mutate} = useMutation({
    mutationKey: ['save-invoice'],
    mutationFn: patchSaveInvoice,
    onSuccess: data => {
      if (data.success) {
        toast(ToastType.Success, 'Invoice saved', data.message);
        queryClient.invalidateQueries({queryKey: ['payment-details']});
        queryClient.invalidateQueries({queryKey: ['generate-invoice']});
        onSuccess?.(data);
      } else {
        toast(ToastType.Error, 'Save invoice failed', data.message);
      }
    },
    onError: error => handleErrorResponse(error, 'Save invoice'),
  });

  const saveInvoice = useCallback(
    (
      variables: ISaveInvoiceVariables & {
        onSuccess?: (response: ISaveInvoiceResponseData) => void;
      }
    ) => {
      const {onSuccess: callback, ...rest} = variables;
      mutate(rest, {
        onSuccess: callback,
      });
    },
    [mutate]
  );

  return {
    isSaving: isPending,
    saveInvoice,
  };
};

export const useSendReceipt = () => {
  const mutation = useMutation<
    {success: boolean; data: []; message: string},
    Error,
    number
  >({
    mutationKey: ['send-receipt'],
    mutationFn: (paymentId: number) => getSendReceipt(paymentId),
    onSuccess: data => {
      toast(ToastType.Success, 'Send receipt', data.message);
    },
    onError: error => handleErrorResponse(error, 'Send receipt'),
  });

  return {
    sendReceipt: mutation.mutateAsync,
    isSending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  };
};
